// src/components/EmployeePersonalSection.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Collapse,
  IconButton,
  Chip,
  Paper,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, auth, functions as cloudFunctions } from "../firebase";
import { resolveTenantId } from "../utils/tenant";
import {
  checkEmployeeEmailAvailable,
  normalizeEmployeeEmail,
  updateEmployeeIdentityTransaction,
} from "../utils/employeeUniqueness";

export default function EmployeePersonalSection({ employeeId, show = "personal" }) {
  const [fields, setFields] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    languages: [],
    emergencyContactName: "",
    emergencyContactRelationship: "",
    emergencyContactPhone: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [originalFields, setOriginalFields] = useState(null);
  const [newLanguage, setNewLanguage] = useState("");
  const [emailTaken, setEmailTaken] = useState(false);

  const hasChanges = !!originalFields && JSON.stringify(fields) !== JSON.stringify(originalFields);

  useEffect(() => {
    async function load() {
      setLoading(true);
      if (!employeeId) {
        setLoading(false);
        return;
      }
      // Resolve tenant-scoped employee doc
      let tenantId = null;
      const adminRaw = localStorage.getItem('adminUser');
      if (adminRaw) {
        try { tenantId = JSON.parse(adminRaw).tenantId; } catch (e) { /* ignore */ }
      }
      if (!tenantId && auth && auth.currentUser) {
        const utRef = doc(db, `userTenants/${auth.currentUser.uid}`);
        const utSnap = await getDoc(utRef);
        if (utSnap.exists()) {
          const ut = utSnap.data();
          tenantId = ut.tenantId || (ut.tenantIds && ut.tenantIds[0]);
        }
      }
      if (!tenantId) {
        setLoading(false);
        setErrorMsg('Tenant not found.');
        return;
      }
      const ref = doc(db, `tenants/${tenantId}/employees`, employeeId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const d = snap.data();
        const newFields = {
          firstName: d.firstName || "",
          lastName: d.lastName || "",
          email: d.email || "",
          phone: d.phone || "",
          dateOfBirth: d.dateOfBirth || "",
          languages: d.languages || [],
          emergencyContactName: d.emergencyContactName || "",
          emergencyContactRelationship: d.emergencyContactRelationship || "",
          emergencyContactPhone: d.emergencyContactPhone || "",
        };
        setFields(newFields);
        setOriginalFields(newFields);
      }
      setLoading(false);
    }
    load();
  }, [employeeId]);

  const handleChange = (e) => {
    setFields((f) => ({
      ...f,
      [e.target.name]: e.target.value,
    }));
  };

  const handleAddLanguage = () => {
    if (newLanguage.trim() && !fields.languages.includes(newLanguage.trim())) {
      setFields((f) => ({
        ...f,
        languages: [...f.languages, newLanguage.trim()],
      }));
      setNewLanguage("");
    }
  };

  const handleRemoveLanguage = (languageToRemove) => {
    setFields((f) => ({
      ...f,
      languages: f.languages.filter(lang => lang !== languageToRemove),
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddLanguage();
    }
  };

  const validate = () => {
    if (show === "personal") {
      if (!fields.firstName.trim()) return "First Name is required.";
      if (!fields.lastName.trim()) return "Last Name is required.";
      if (!fields.email.trim()) return "Email is required.";
      // Basic email pattern
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email.trim())) return "Email is invalid.";
      if (emailTaken) return "Email is already in use.";
    }
    if (show === "emergency") {
      if (!fields.emergencyContactName.trim()) return "Emergency Contact Name is required.";
    }
    return "";
  };

  const handleEmailBlur = async () => {
    if (show !== 'personal') return;
    const normalized = normalizeEmployeeEmail(fields.email);
    if (!normalized) {
      setEmailTaken(false);
      return;
    }
    try {
      const tenantId = await resolveTenantId();
      const res = await checkEmployeeEmailAvailable({ tenantId, email: normalized, excludeEmployeeId: employeeId });
      setEmailTaken(!res.available);
    } catch (e) {
      // If tenant can't be resolved or read fails, don't block editing; transaction will enforce on save.
      setEmailTaken(false);
    }
  };

  const handleSave = async () => {
    const error = validate();
    if (error) {
      setErrorMsg(error);
      setSuccessMsg("");
      return;
    }
    setSaving(true);
    setErrorMsg("");
    try {
      const tenantId = await resolveTenantId();
      const normalizedCurrentEmail = normalizeEmployeeEmail(originalFields?.email || "");
      const normalizedNextEmail = normalizeEmployeeEmail(fields.email);
      const emailChanged = show === "personal" && normalizedCurrentEmail !== normalizedNextEmail;
      const normalizedFields = {
        ...fields,
        email: normalizedNextEmail,
      };
      const originalWithoutEmail = { ...(originalFields || {}) };
      const nextWithoutEmail = { ...normalizedFields };
      delete originalWithoutEmail.email;
      delete nextWithoutEmail.email;
      const nonEmailChanged =
        JSON.stringify(originalWithoutEmail) !== JSON.stringify(nextWithoutEmail);

      if (emailChanged) {
        if (!cloudFunctions) {
          throw new Error("Email change function is unavailable.");
        }
        const changeEmployeeEmail = httpsCallable(cloudFunctions, "changeEmployeeEmail");
        await changeEmployeeEmail({
          tenantId,
          employeeId,
          newEmail: normalizedNextEmail,
        });
      }

      // Use a transaction so updates are atomic and uniqueness is enforced at the database level.
      if (!emailChanged || nonEmailChanged) {
        await updateEmployeeIdentityTransaction({
          tenantId,
          employeeId,
          updates: emailChanged ? nextWithoutEmail : normalizedFields,
        });
      }

      const savedFields = normalizedFields;
      setSuccessMsg('Saved!');
      setEdit(false);
      setFields(savedFields);
      setOriginalFields(savedFields);
      setEmailTaken(false);
    } catch (err) {
      // Map common Firestore permission error to a friendlier message
      if (err && err.code === 'permission-denied') {
        setErrorMsg('Failed to save: Missing or insufficient permissions.');
      } else if (err && err.code === 'functions/permission-denied') {
        setErrorMsg('Failed to save: You do not have permission to change this email.');
      } else if (err && (err.code === 'functions/already-exists' || err.code === 'already-exists')) {
        setEmailTaken(true);
        setErrorMsg('Failed to save: This email is already in use.');
      } else if (err && err.message === 'employee-email-taken') {
        setEmailTaken(true);
        setErrorMsg('Failed to save: Email is already in use in this tenant.');
      } else if (err && err.message && err.message.includes('Tenant not found')) {
        setErrorMsg('Failed to save: Tenant not found. Please re-login.');
      } else {
        setErrorMsg('Failed to save: ' + (err.message || 'Unknown error.'));
      }
    }
    setSaving(false);
  };

  const handleCancel = () => {
    setFields(originalFields || fields);
    setEdit(false);
    setErrorMsg("");
    setSuccessMsg("");
    setNewLanguage("");
  };

  if (loading) return <Typography>Loading...</Typography>;

  const sectionTitle = show === "personal" ? "Personal Information" : "Emergency Contact";
  const sectionTint = show === "personal" ? 'rgba(25, 118, 210, 0.04)' : 'rgba(46, 125, 50, 0.04)';
  const sectionDescription = show === "personal"
    ? "Core identity, contact details, and spoken languages."
    : "Who to contact quickly when an issue comes up.";
  const displayFields = show === "personal"
    ? [
        { label: "First Name", value: fields.firstName || "Not provided" },
        { label: "Last Name", value: fields.lastName || "Not provided" },
        { label: "Email", value: fields.email || "Not provided" },
        { label: "Phone Number", value: fields.phone || "No phone number on file" },
        { label: "Date of Birth", value: fields.dateOfBirth || "Not provided" },
      ]
    : [
        { label: "Contact Name", value: fields.emergencyContactName || "Not provided" },
        { label: "Relationship", value: fields.emergencyContactRelationship || "Not provided" },
        { label: "Contact Phone Number", value: fields.emergencyContactPhone || "No phone number on file" },
      ];

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: 1120,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: sectionTint,
        overflow: 'hidden',
        boxShadow: '0 12px 28px rgba(15, 23, 42, 0.05)',
      }}
    >
      <Box sx={{ px: 2.25, py: 1.5, bgcolor: 'rgba(255,255,255,0.68)', backdropFilter: 'blur(4px)', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900, fontSize: '1.08rem' }}>
              {sectionTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: '0.88rem' }}>
              {sectionDescription}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {!edit ? (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setEdit(true);
                  setSuccessMsg("");
                  setErrorMsg("");
                  setOriginalFields(fields);
                }}
                sx={{
                  borderRadius: 2.5,
                  textTransform: 'none',
                  fontWeight: 800,
                  px: 1.25,
                  minHeight: 36,
                }}
              >
                ✏️ Edit
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ px: 2.25, pb: 2.25, pt: 1 }}>
      <Collapse in={!!successMsg || !!errorMsg} sx={{ mt: 1 }}>
        {successMsg && (
          <Alert
            severity="success"
            action={
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={() => setSuccessMsg("")}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
            sx={{ mb: 1 }}
          >
            {successMsg}
          </Alert>
        )}
        {errorMsg && (
          <Alert
            severity="error"
            action={
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={() => setErrorMsg("")}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
            sx={{ mb: 1 }}
          >
            {errorMsg}
          </Alert>
        )}
      </Collapse>
  {!edit ? (
        <Stack spacing={2.25} mt={1.5}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: show === 'personal' ? 'repeat(3, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))' },
              gap: 1.1,
            }}
          >
            {displayFields.map((item) => (
              <Box
                key={item.label}
                sx={{
                  p: 1.2,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: alpha('#ffffff', 0.72),
                  gridColumn: item.wide ? { md: 'span 2' } : undefined,
                }}
              >
                <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.6, fontSize: '0.68rem', lineHeight: 1.1 }}>
                  {item.label}
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.32, fontWeight: 700, lineHeight: 1.35, fontSize: '1rem' }}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>

          {show === "personal" && (
            <Box
              sx={{
                p: 1.35,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: alpha('#ffffff', 0.72),
              }}
            >
              <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: 0.6, fontSize: '0.68rem', lineHeight: 1.1 }}>
                Languages Spoken
              </Typography>
              {fields.languages.length > 0 ? (
                <Box sx={{ mt: 0.7, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {fields.languages.map((language, index) => (
                    <Chip
                      key={index}
                      label={language}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        bgcolor: alpha('#2563eb', 0.08),
                        color: 'primary.main',
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.55, fontSize: '0.92rem' }}>
                  No languages specified
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      ) : (
      <Stack spacing={2} mt={2}>
        {show === "personal" && (
          <>
            <Stack direction="row" spacing={2}>
              <TextField
                label="First Name"
                name="firstName"
                value={fields.firstName}
                onChange={handleChange}
                fullWidth
                disabled={!edit}
                required
              />
              <TextField
                label="Last Name"
                name="lastName"
                value={fields.lastName}
                onChange={handleChange}
                fullWidth
                disabled={!edit}
                required
              />
            </Stack>
            <TextField
              label="Email"
              name="email"
              value={fields.email}
              onChange={handleChange}
              onBlur={handleEmailBlur}
              fullWidth
              disabled={!edit}
              required
              type="email"
              error={edit && !!fields.email && emailTaken}
              helperText={edit && !!fields.email && emailTaken ? 'Email is already in use' : ''}
            />
            <TextField
              label="Phone Number"
              name="phone"
              value={fields.phone}
              onChange={handleChange}
              fullWidth
              disabled={!edit}
            />
            <TextField
              label="Date of Birth"
              name="dateOfBirth"
              value={fields.dateOfBirth}
              onChange={handleChange}
              fullWidth
              disabled={!edit}
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
            />
            
            {/* Languages Section */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Languages Spoken
              </Typography>
              {fields.languages.length > 0 && (
                <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {fields.languages.map((language, index) => (
                    <Chip
                      key={index}
                      label={language}
                      onDelete={edit ? () => handleRemoveLanguage(language) : undefined}
                      deleteIcon={<DeleteIcon />}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}
              {edit && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    label="Add Language"
                    value={newLanguage}
                    onChange={(e) => setNewLanguage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    size="small"
                    placeholder="e.g., English, Spanish"
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAddLanguage}
                    disabled={!newLanguage.trim()}
                    startIcon={<AddIcon />}
                  >
                    Add
                  </Button>
                </Box>
              )}
              {!edit && fields.languages.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No languages specified
                </Typography>
              )}
            </Box>
          </>
        )}
        {show === "emergency" && (
          <>
            <TextField
              label="Contact Name"
              name="emergencyContactName"
              value={fields.emergencyContactName}
              onChange={handleChange}
              fullWidth
              disabled={!edit}
              required
            />
            <TextField
              label="Relationship"
              name="emergencyContactRelationship"
              value={fields.emergencyContactRelationship}
              onChange={handleChange}
              fullWidth
              disabled={!edit}
            />
            <TextField
              label="Contact Phone Number"
              name="emergencyContactPhone"
              value={fields.emergencyContactPhone}
              onChange={handleChange}
              fullWidth
              disabled={!edit}
            />
          </>
        )}
      </Stack>
      )}

      {/* Unsaved changes bar */}
      {edit && hasChanges && (
        <Paper
          elevation={2}
          sx={{
            position: 'sticky',
            bottom: 0,
            mt: 2.5,
            p: 1.25,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            zIndex: 1,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Unsaved changes
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" size="small" onClick={handleCancel} disabled={saving}>
                Cancel
              </Button>
              <Button variant="contained" size="small" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}
      </Box>
    </Paper>
  );
}
